import { useCallback, useEffect, useState } from 'react';
import { api, type Anomalia } from '../api';
import { etiquetaDeCanal } from '../canales';

/**
 * Panel de anomalías.
 *
 * Va arriba de todo y no en una pestaña propia porque responde a la pregunta
 * con la que alguien abre el dashboard: qué mirar. Una alerta escondida detrás
 * de un clic es una alerta que se ve tarde.
 *
 * Cuando no hay nada, no ocupa espacio: un panel que dice «todo bien» todos los
 * días entrena a la gente a ignorar esa zona de la pantalla.
 */

const TITULO_METRICA: Record<string, string> = {
  tasa_error: 'Errores',
  tasa_abandono: 'Abandono',
  latencia_p95: 'Latencia',
  vital_p75: 'Rendimiento',
};

export function PanelAnomalias({
  onVerProceso,
  recarga,
}: {
  onVerProceso: (proceso: string) => void;
  /** Cambia cada vez que la vista se recarga. Solo se usa para volver a pedir. */
  recarga: number;
}) {
  const [anomalias, setAnomalias] = useState<Anomalia[] | null>(null);

  const cargar = useCallback(async () => {
    try {
      setAnomalias((await api.anomalias()).anomalias);
    } catch {
      // Que falle la detección no puede tapar el resto del dashboard.
      setAnomalias([]);
    }
  }, []);

  // La lista anterior se deja en pantalla mientras llega la nueva: parpadear a
  // vacío y volver haría dudar de lo que se está leyendo.
  useEffect(() => {
    void cargar();
  }, [cargar, recarga]);

  if (!anomalias || anomalias.length === 0) return null;

  return (
    <section className="anomalias">
      <h3>
        Requiere atención
        <span className="cuenta">{anomalias.length}</span>
      </h3>

      <ul>
        {anomalias.map((a, i) => (
          <li key={`${a.ambito}-${a.metrica}-${i}`} className={`anomalia ${a.severidad}`}>
            <div className="anomalia-cabecera">
              <span className="etiqueta-metrica">{TITULO_METRICA[a.metrica] ?? a.metrica}</span>
              <button className="enlace" onClick={() => onVerProceso(a.ambito)}>
                {a.ambito}
              </button>
              <span className={`canal ${a.canal}`}>{etiquetaDeCanal(a.canal)}</span>
              <span className={`marca-tipo ${a.tipo}`}>
                {a.tipo === 'version' ? 'tras un despliegue' : 'contra lo habitual'}
              </span>
            </div>
            <p className="anomalia-texto">{a.explicacion}</p>
            <p className="anomalia-detalle">{a.detalle}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}
